export const PULSE_CARDS_STORAGE_KEY = 'gyro.pulse.cards';
export const PULSE_CARDS_DEFAULT_PER_DAY = 10;
/** The most dismissals the device remembers (an action id each); the oldest go first. */
export const PULSE_DISMISSED_MAX = 200;
/** The card's words (contract section 10.2 and 10.3), exactly as people read them. */
export const PULSE_CARD_QUESTION = 'How did that go?';
export const PULSE_RATING_LABELS = Object.freeze({ worked: 'Worked', partly: 'Partly', failed: "Didn't work" });
export const PULSE_UNDO_LABEL = 'Had to undo it';
export const PULSE_CARD_PLACEHOLDER = 'How could this be better? (optional)';
export const PULSE_THUMB_PLACEHOLDER = 'What would have made this better? (optional)';
export const PULSE_SEND_LABEL = 'Send';
export const PULSE_THANKS_TEXT = 'Thanks.';
export const PULSE_SEND_FAILED_TEXT = 'Could not send that. Try again.';
/** How long "Thanks." stays before the card goes (section 10.2: removed after 2 s). */
export const PULSE_THANKS_MS = 2000;
/** Below this width the buttons wrap under the question (the 320 px Office pane; section 10.2). */
export const PULSE_NARROW_PX = 420;
export const PULSE_RATINGS = ['worked', 'partly', 'failed'];
/** The `pulse` block of the status frame, validated: null unless the cards are on. `perDay` falls back to 10. */
export function pulseStatus(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const block = value;
    if (block.cards !== true)
        return null;
    const perDay = typeof block.perDay === 'number' && Number.isInteger(block.perDay) && block.perDay > 0 && block.perDay <= 1000 ? block.perDay : PULSE_CARDS_DEFAULT_PER_DAY;
    return { cards: true, perDay };
}
/** The device's calendar day for `nowMs` (the person's day, not UTC: the cap is theirs). */
export function pulseDay(nowMs) {
    const date = new Date(nowMs);
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
export function emptyPulseCardsState(day) {
    return { day, shown: 0, intents: [], dismissed: [] };
}
const strings = (value, max, each = 200) => Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.length > 0 && item.length <= each).slice(-max) : [];
/**
 * A stored state, validated, rolled over to `day` when it is from another day: the count and the intents start again,
 * the dismissals stay (an action dismissed late yesterday stays dismissed when the inbox re-reports it today).
 */
export function normalizePulseCardsState(value, day) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return emptyPulseCardsState(day);
    const stored = value;
    const dismissed = strings(stored.dismissed, PULSE_DISMISSED_MAX);
    if (stored.day !== day)
        return { ...emptyPulseCardsState(day), dismissed };
    return {
        day,
        shown: typeof stored.shown === 'number' && Number.isInteger(stored.shown) && stored.shown >= 0 ? stored.shown : 0,
        intents: strings(stored.intents, 1000, 80),
        dismissed,
    };
}
export function readPulseCardsState(storage, nowMs) {
    const day = pulseDay(nowMs);
    try {
        return normalizePulseCardsState(JSON.parse(storage?.getItem(PULSE_CARDS_STORAGE_KEY) || 'null'), day);
    }
    catch {
        return emptyPulseCardsState(day);
    }
}
export function writePulseCardsState(storage, state) {
    if (!storage)
        return false;
    try {
        storage.setItem(PULSE_CARDS_STORAGE_KEY, JSON.stringify(normalizePulseCardsState(state, state.day)));
        return true;
    }
    catch {
        return false;
    }
}
export function pulseCardStore(storage, now = Date.now) {
    const read = () => readPulseCardsState(storage, now());
    const save = (state) => { writePulseCardsState(storage, state); return state; };
    return {
        read,
        shown: (intentKey) => {
            const state = read();
            return save({ ...state, shown: state.shown + 1, intents: state.intents.includes(intentKey) ? state.intents : [...state.intents, intentKey] });
        },
        dismissed: (actionKey) => {
            const state = read();
            return save({ ...state, dismissed: [...state.dismissed.filter((key) => key !== actionKey), actionKey].slice(-PULSE_DISMISSED_MAX) });
        },
    };
}
/** Whether a card follows this settled action (section 10.1), and if not, the first rule that says no. */
export function pulseCardDecision(candidate, conditions, state, cardedThisSession) {
    const no = (reason) => ({ show: false, reason });
    if (!conditions.status?.cards)
        return no('off');
    if (!candidate.askId)
        return no('no-ask');
    if (!candidate.highProfile)
        return no('not-high-profile');
    if (cardedThisSession.has(candidate.actionKey))
        return no('already-carded');
    if (state.dismissed.includes(candidate.actionKey))
        return no('dismissed');
    if (state.shown >= conditions.status.perDay)
        return no('cap');
    if (candidate.intentKey && state.intents.includes(candidate.intentKey))
        return no('intent-today');
    if (conditions.classified)
        return no('classified');
    if (conditions.busy)
        return no('busy');
    if (conditions.dictating)
        return no('dictating');
    if (conditions.paneEditApplying)
        return no('pane-editing');
    return { show: true };
}
/**
 * The device's key for an intent (the rule of contract section 2 applied to what the client has: the tool and the
 * action's summary): lower case, numbers as N, punctuation removed, spaces as dashes, at most 80 characters.
 */
export function pulseIntentKey(tool, summary) {
    const words = `${tool} ${summary}`.toLowerCase().replace(/\d+([.,]\d+)*/g, 'N').replace(/[^a-z0-9N\s_-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
    return words.join('-').slice(0, 80).replace(/-+$/, '');
}
const DEFAULT_CHIPS = Object.freeze({
    revit: ['Wrong elements', 'Nothing changed', 'Revit showed an error'],
    revu: ['Wrong elements', 'Nothing changed', 'Revu showed an error'],
    ppp: ['A section was thin', 'Wrong period', 'Export failed'],
    word: ['Changed the wrong text', 'Nothing changed', 'Broke the formatting'],
    documents: ['Wrong content', 'File would not open', 'Missing part'],
    mail: ['Wrong recipient or time', 'Nothing sent', 'Wrong text'],
    starscribe: ['Changed the wrong part', 'Nothing changed', 'Lost my text'],
    other: ['Wrong result', 'Nothing happened', 'Showed an error'],
});
/** The chip key the client asks and caches by: the tool, and for `mcp_call` the server after a slash (`mcp_call/<server>`). */
export function pulseChipKey(tool, server) {
    const name = tool.trim().slice(0, 80);
    const host = typeof server === 'string' ? server.trim().slice(0, 80) : '';
    return host && (name === 'mcp_call' || name.startsWith('mcp_')) ? `${name}/${host}` : name;
}
export function pulseChipFamily(chipKey) {
    const key = chipKey.toLowerCase();
    if (key.includes('revit'))
        return 'revit';
    if (key.includes('revu') || key.includes('bluebeam'))
        return 'revu';
    if (key.startsWith('ppp'))
        return 'ppp';
    if (key.startsWith('word_'))
        return 'word';
    if (key.startsWith('starscribe'))
        return 'starscribe';
    if (/mail|event|calendar|teams|message|invite/.test(key))
        return 'mail';
    if (/document|file|excel_|ppt_|powerpoint|export|code|artifact|sheet|slide/.test(key))
        return 'documents';
    return 'other';
}
/** The tool family's three default chips (section 10.4), for when the service has no learned ones or is dark. */
export function defaultPulseChips(chipKey) {
    return [...DEFAULT_CHIPS[pulseChipFamily(chipKey)]];
}
/** `GET /v1/pulse/chips` answered: `{ chips: string[] }` or a bare array of 1 to 3 short strings; anything else is null. */
export function parsePulseChips(value) {
    const list = Array.isArray(value) ? value : value && typeof value === 'object' && Array.isArray(value.chips) ? value.chips : null;
    if (!list)
        return null;
    const chips = list.filter((item) => typeof item === 'string' && item.trim().length > 0 && item.length <= 60).map((item) => item.trim());
    return chips.length >= 1 && chips.length <= 3 && new Set(chips).size === chips.length ? chips : null;
}
/** The Office pane's hook (section 10.2): the pane announces a settled higher-profile write with this event. */
export const PULSE_PANE_SETTLED_EVENT = 'gyro:pulse-tool-settled';
const TOOL_NAME = /^[a-z][a-z0-9_]{1,79}$/;
/** The event's detail, validated (any script in the page can dispatch it): a tool name and an outcome, nothing else is read. */
export function parsePulseToolSettled(detail) {
    if (!detail || typeof detail !== 'object')
        return null;
    const { tool, ok } = detail;
    if (typeof tool !== 'string' || !TOOL_NAME.test(tool) || typeof ok !== 'boolean')
        return null;
    return { tool, ok };
}
const PANE_SUMMARIES = Object.freeze({
    word_edit_text: 'Edited the document',
    word_replace_paragraphs: 'Replaced paragraphs in the document',
    excel_write_range: 'Wrote to the sheet',
    ppt_insert_slides: 'Inserted slides',
    ppt_update_slide: 'Updated a slide',
});
/** What the pane's card is labelled for screen readers (the Done line above names the edit for everyone else). */
export function paneToolSummary(tool) {
    return PANE_SUMMARIES[tool] ?? 'The last edit';
}
//# sourceMappingURL=pulseCards.js.map